/**
 * Program eligibility and audience access control utilities.
 */

export const AUDIENCE_TIERS = {
    PUBLIC: 'public',
    COMMUNITY: 'community',
    SCOPED: 'scoped'
};

export const AUDIENCE_LABELS = {
    [AUDIENCE_TIERS.PUBLIC]: 'Open to Everyone',
    [AUDIENCE_TIERS.COMMUNITY]: 'TAS Community Only',
    [AUDIENCE_TIERS.SCOPED]: 'Enrolled Members Only'
};

export const AUDIENCE_BADGE_COLORS = {
    [AUDIENCE_TIERS.PUBLIC]: 'success',
    [AUDIENCE_TIERS.COMMUNITY]: 'info',
    [AUDIENCE_TIERS.SCOPED]: 'warning'
};

/**
 * Returns a human-friendly display label for an audience tier.
 * @param {string} tier
 * @returns {string}
 */
export const getAudienceLabel = (tier) => {
    return AUDIENCE_LABELS[tier] || AUDIENCE_LABELS[AUDIENCE_TIERS.PUBLIC];
};

const checkCommunityEligibility = (user, userAccount, role) => {
    if (!user) {
        return {
            eligible: false,
            tier: AUDIENCE_TIERS.COMMUNITY,
            reason: 'login_required',
            message: 'This program is exclusive to the TAS Community. Please sign in to register.'
        };
    }
    const isCommunity = userAccount?.isCommunityMember === 'Yes' || role === 'admin' || role === 'coach';
    if (!isCommunity) {
        return {
            eligible: false,
            tier: AUDIENCE_TIERS.COMMUNITY,
            reason: 'community_required',
            message: 'This program is exclusive to verified TAS Community members. Please contact administration for community access.'
        };
    }
    return { eligible: true, tier: AUDIENCE_TIERS.COMMUNITY };
};

const checkScopedParentPrograms = (program, parentIds, enrolledProgramIds) => {
    if (parentIds.includes('public') || parentIds.length === 0) {
        return { eligible: true, tier: AUDIENCE_TIERS.SCOPED };
    }

    if (parentIds.includes('all_programs')) {
        if (enrolledProgramIds.length > 0) {
            return { eligible: true, tier: AUDIENCE_TIERS.SCOPED };
        }
        return {
            eligible: false,
            tier: AUDIENCE_TIERS.SCOPED,
            reason: 'not_enrolled',
            parentProgramIds: parentIds,
            message: 'This program is restricted to athletes actively enrolled in a TAS seasonal program.'
        };
    }

    const enrolledSet = new Set(enrolledProgramIds);
    if (parentIds.some(pid => enrolledSet.has(pid))) {
        return { eligible: true, tier: AUDIENCE_TIERS.SCOPED };
    }

    const parentNames = Array.isArray(program.parentProgramNames) && program.parentProgramNames.length > 0
        ? program.parentProgramNames.join(', ')
        : '';
    return {
        eligible: false,
        tier: AUDIENCE_TIERS.SCOPED,
        reason: 'not_enrolled',
        parentProgramIds: parentIds,
        message: parentNames
            ? `This program is exclusive to families with athletes enrolled in ${parentNames}.`
            : 'This program is restricted to athletes enrolled in designated parent programs.'
    };
};

const checkScopedEligibility = (program, user, enrolledProgramIds) => {
    const parentIds = Array.isArray(program.parentProgramIds) ? program.parentProgramIds : [];
    if (!user) {
        return {
            eligible: false,
            tier: AUDIENCE_TIERS.SCOPED,
            reason: 'login_required',
            parentProgramIds: parentIds,
            message: 'This program is restricted to enrolled seasonal program members. Please sign in to verify your enrollment.'
        };
    }

    return checkScopedParentPrograms(program, parentIds, enrolledProgramIds);
};

/**
 * Checks audience eligibility for a program/event/camp based on audience tier and parent program associations.
 *
 * @param {Object} program
 * @param {Object} options
 * @param {Object|null} [options.user] - Firebase Auth user
 * @param {Object|null} [options.userAccount] - Firestore user account document
 * @param {Array<string>} [options.enrolledProgramIds] - List of program IDs the user/family is enrolled in
 * @returns {{ eligible: boolean, tier: string, reason?: string, message?: string, parentProgramIds?: Array<string> }}
 */
const resolveTierEligibility = (program, user, userAccount, enrolledProgramIds, tier) => {
    if (tier === AUDIENCE_TIERS.COMMUNITY) {
        return checkCommunityEligibility(user, userAccount, userAccount?.role);
    }
    if (tier === AUDIENCE_TIERS.SCOPED || tier === 'enrolled_athletes') {
        return checkScopedEligibility(program, user, enrolledProgramIds);
    }
    return { eligible: true, tier };
};

export const checkAudienceEligibility = (program, options = {}) => {
    if (!program) return { eligible: true, tier: AUDIENCE_TIERS.PUBLIC };

    const { user = null, userAccount = null, enrolledProgramIds = [] } = options || {};

    const role = userAccount?.role;
    if (role === 'athlete') {
        return {
            eligible: false,
            tier: program.audience || AUDIENCE_TIERS.PUBLIC,
            reason: 'athlete_restricted',
            message: 'Athlete accounts cannot register for programs or sign legal waivers. Please ask your parent or legal guardian to log in to register.'
        };
    }

    const tier = program.audience || AUDIENCE_TIERS.PUBLIC;
    if (tier === AUDIENCE_TIERS.PUBLIC) {
        return { eligible: true, tier: AUDIENCE_TIERS.PUBLIC };
    }

    return resolveTierEligibility(program, user, userAccount, enrolledProgramIds, tier);
};

const extractEarlyAccessIds = (userAccountOrEarlyAccessIds) => {
    if (Array.isArray(userAccountOrEarlyAccessIds)) {
        return [...userAccountOrEarlyAccessIds];
    }
    if (typeof userAccountOrEarlyAccessIds !== 'object' || !userAccountOrEarlyAccessIds) {
        return [];
    }

    const ids = [];
    const directLists = userAccountOrEarlyAccessIds.earlyAccessProgramIds || userAccountOrEarlyAccessIds.earlyAccessPrograms;
    if (Array.isArray(directLists)) {
        ids.push(...directLists);
    }

    if (userAccountOrEarlyAccessIds.earlyAccessDiscounts && typeof userAccountOrEarlyAccessIds.earlyAccessDiscounts === 'object') {
        Object.keys(userAccountOrEarlyAccessIds.earlyAccessDiscounts).forEach(k => {
            const pKey = k.includes('_') ? k.split('_')[0] : k;
            if (pKey) ids.push(pKey);
        });
    }

    return ids;
};

const cleanProgramSlug = (val) => (val || '').toLowerCase().replace(/^\/?(programs|camps|races)\//, '').replace(/[^\w-]+/g, '-');
const slugifyText = (text) => (text || '').toLowerCase().replace(/[^\w-]+/g, '-');

const buildProgramKeysSet = (program) => {
    const prevSlugs = Array.isArray(program.previousSlugs) ? program.previousSlugs : [];
    return new Set([
        program.id,
        program.link,
        program.slug,
        program.id ? cleanProgramSlug(program.id) : null,
        program.link ? cleanProgramSlug(program.link) : null,
        program.slug ? cleanProgramSlug(program.slug) : null,
        program.title ? slugifyText(program.title) : null,
        program.title ? cleanProgramSlug(program.title) : null,
        ...prevSlugs,
        ...prevSlugs.map(cleanProgramSlug)
    ].filter(Boolean));
};

/**
 * Checks whether an account has early or late access override for a program.
 *
 * @param {Object} program
 * @param {Object|Array} userAccountOrEarlyAccessIds
 * @returns {boolean}
 */
export const hasEarlyAccessForProgram = (program, userAccountOrEarlyAccessIds = null) => {
    if (!program || !userAccountOrEarlyAccessIds) return false;
    const earlyAccessIds = extractEarlyAccessIds(userAccountOrEarlyAccessIds);
    if (earlyAccessIds.length === 0) return false;

    const programKeys = buildProgramKeysSet(program);

    return earlyAccessIds.some(id => {
        if (!id) return false;
        return programKeys.has(id) || programKeys.has(cleanProgramSlug(id)) || programKeys.has(slugifyText(id));
    });
};

/**
 * Generate a unique guest identifier for public event/camp registrations
 * @returns {string}
 */
export const generateGuestUid = () => {
    return `guest_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
};
